import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, CheckCircle, Plus, Trash2 } from 'lucide-react';
import apiService from '../services/api';
import { Blog } from '../types';

const CATEGORIES = [
  'Mobile Repair', 'Battery Replacement', 'Screen Repair', 'iPhone',
  'Samsung', 'OnePlus', 'Xiaomi', 'Business', 'Tips & Tricks',
];

interface FaqItem { q: string; a: string; }

const slugify = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();

const BlogFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id }   = useParams<{ id: string }>();
  const isEdit   = Boolean(id);

  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [faqs, setFaqs]       = useState<FaqItem[]>([{ q: '', a: '' }]);

  const [form, setForm] = useState<Partial<Blog>>({
    title:          '',
    slug:           '',
    excerpt:        '',
    content:        '',
    imageUrl:       '',
    author:         'Minto Repairs Team',
    category:       'Mobile Repair',
    tags:           '',
    status:         'draft',
    isFeatured:     false,
    isTrending:     false,
    seoTitle:       '',
    seoDescription: '',
    seoKeywords:    '',
    focusKeyword:   '',
    canonicalUrl:   '',
    city:           '',
    area:           '',
    device:         '',
    service:        '',
  });

  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        const res = await apiService.getBlog(Number(id));
        if (res.isSuccess && res.entity) {
          setForm(res.entity);
          try {
            const parsed = JSON.parse(res.entity.faq || '[]');
            if (Array.isArray(parsed) && parsed.length) setFaqs(parsed);
          } catch { /* ignore */ }
        } else {
          toast.error('Blog not found');
          navigate('/blogs');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const set = (field: keyof Blog, value: any) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleTitleChange = (title: string) => {
    setForm(prev => ({
      ...prev,
      title,
      slug:       prev.slug || slugify(title),
      seoTitle:   prev.seoTitle || title,
      canonicalUrl: prev.canonicalUrl || `https://www.mintorepairs.com/blog/${slugify(title)}/`,
    }));
  };

  const handleSave = async (publishAfter = false) => {
    if (!form.title?.trim()) { toast.error('Title is required'); return; }
    if (!form.slug?.trim())  { toast.error('Slug is required');  return; }
    if (!form.content?.trim()) { toast.error('Content is required'); return; }

    setSaving(true);
    const payload: Partial<Blog> = {
      ...form,
      faq:          faqs.filter(f => f.q.trim()).length ? JSON.stringify(faqs.filter(f => f.q.trim())) : undefined,
      canonicalUrl: form.canonicalUrl || `https://www.mintorepairs.com/blog/${form.slug}/`,
    };

    try {
      const res = isEdit
        ? await apiService.updateBlog(Number(id), payload)
        : await apiService.createBlog(payload);

      if (!res.isSuccess) { toast.error(res.message || 'Save failed'); return; }

      if (publishAfter) {
        const blogId = res.entity?.id || Number(id);
        await apiService.publishBlog(blogId);
        toast.success('Blog saved and published! Rebuild Next.js to go live.');
      } else {
        toast.success(isEdit ? 'Blog updated' : 'Blog created');
      }
      navigate('/blogs');
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addFaq  = () => setFaqs(f => [...f, { q: '', a: '' }]);
  const delFaq  = (i: number) => setFaqs(f => f.filter((_, idx) => idx !== i));
  const setFaq  = (i: number, field: 'q' | 'a', val: string) =>
    setFaqs(f => f.map((item, idx) => idx === i ? { ...item, [field]: val } : item));

  if (loading) return <div className="p-8 text-center text-slate-400">Loading…</div>;

  const fieldCls = "w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-primary-500";
  const labelCls = "block text-xs font-medium text-slate-400 mb-1";
  const sectionCls = "bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4";

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/blogs')} className="p-2 text-slate-400 hover:text-white">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">{isEdit ? 'Edit Blog' : 'New Blog'}</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm font-medium disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Draft'}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 text-sm font-medium disabled:opacity-50"
          >
            <CheckCircle className="h-4 w-4" /> Publish
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main content — left 2 cols */}
        <div className="lg:col-span-2 space-y-5">

          {/* Core */}
          <div className={sectionCls}>
            <h2 className="text-sm font-semibold text-slate-300 border-b border-slate-800 pb-2">Content</h2>
            <div>
              <label className={labelCls}>Title *</label>
              <input type="text" className={fieldCls} placeholder="Blog title" value={form.title || ''} onChange={e => handleTitleChange(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Slug *</label>
              <input type="text" className={fieldCls} placeholder="url-friendly-slug" value={form.slug || ''}
                onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))} />
              <p className="text-xs text-slate-500 mt-1">mintorepairs.com/blog/<strong>{form.slug || 'slug'}</strong>/</p>
            </div>
            <div>
              <label className={labelCls}>Excerpt</label>
              <textarea rows={2} className={fieldCls} placeholder="Short summary shown in listing" value={form.excerpt || ''} onChange={e => set('excerpt', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Content * (Markdown supported)</label>
              <textarea rows={18} className={`${fieldCls} font-mono text-xs leading-relaxed`} placeholder="Write your blog content here…" value={form.content || ''} onChange={e => set('content', e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">{(form.content || '').split(/\s+/).filter(Boolean).length} words</p>
            </div>
          </div>

          {/* FAQs */}
          <div className={sectionCls}>
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <h2 className="text-sm font-semibold text-slate-300">FAQs</h2>
              <button onClick={addFaq} className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300">
                <Plus className="h-3.5 w-3.5" /> Add FAQ
              </button>
            </div>
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-2 border border-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-medium">Q{i + 1}</span>
                  <input type="text" className={`${fieldCls} flex-1`} placeholder="Question" value={faq.q} onChange={e => setFaq(i, 'q', e.target.value)} />
                  <button onClick={() => delFaq(i)} className="text-red-400 hover:text-red-300 shrink-0"><Trash2 className="h-4 w-4" /></button>
                </div>
                <textarea rows={2} className={fieldCls} placeholder="Answer" value={faq.a} onChange={e => setFaq(i, 'a', e.target.value)} />
              </div>
            ))}
          </div>

          {/* SEO */}
          <div className={sectionCls}>
            <h2 className="text-sm font-semibold text-slate-300 border-b border-slate-800 pb-2">SEO</h2>
            <div>
              <label className={labelCls}>SEO Title</label>
              <input type="text" className={fieldCls} placeholder="SEO title (defaults to post title)" value={form.seoTitle || ''} onChange={e => set('seoTitle', e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">{(form.seoTitle || '').length}/60 chars</p>
            </div>
            <div>
              <label className={labelCls}>Meta Description</label>
              <textarea rows={2} className={fieldCls} placeholder="Meta description (140–160 chars)" value={form.seoDescription || ''} onChange={e => set('seoDescription', e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">{(form.seoDescription || '').length}/160 chars</p>
            </div>
            <div>
              <label className={labelCls}>SEO Keywords</label>
              <input type="text" className={fieldCls} placeholder="keyword1, keyword2, keyword3" value={form.seoKeywords || ''} onChange={e => set('seoKeywords', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Focus Keyword</label>
              <input type="text" className={fieldCls} placeholder="Primary target keyword" value={form.focusKeyword || ''} onChange={e => set('focusKeyword', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Canonical URL</label>
              <input type="text" className={fieldCls} placeholder={`https://www.mintorepairs.com/blog/${form.slug || 'slug'}/`} value={form.canonicalUrl || ''} onChange={e => set('canonicalUrl', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Sidebar — right 1 col */}
        <div className="space-y-5">

          {/* Publish settings */}
          <div className={sectionCls}>
            <h2 className="text-sm font-semibold text-slate-300 border-b border-slate-800 pb-2">Settings</h2>
            <div>
              <label className={labelCls}>Status</label>
              <select className={fieldCls} value={form.status || 'draft'} onChange={e => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select className={fieldCls} value={form.category || 'Mobile Repair'} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Author</label>
              <input type="text" className={fieldCls} value={form.author || ''} onChange={e => set('author', e.target.value)} />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.isFeatured} onChange={e => set('isFeatured', e.target.checked)} className="rounded border-slate-600 bg-slate-800 text-primary-500" />
                <span className="text-sm text-slate-300">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.isTrending} onChange={e => set('isTrending', e.target.checked)} className="rounded border-slate-600 bg-slate-800 text-primary-500" />
                <span className="text-sm text-slate-300">Trending</span>
              </label>
            </div>
          </div>

          {/* Media */}
          <div className={sectionCls}>
            <h2 className="text-sm font-semibold text-slate-300 border-b border-slate-800 pb-2">Media</h2>
            <div>
              <label className={labelCls}>Featured Image URL</label>
              <input type="text" className={fieldCls} placeholder="https://…/image.png" value={form.imageUrl || ''} onChange={e => set('imageUrl', e.target.value)} />
            </div>
            {form.imageUrl && (
              <img src={form.imageUrl} alt="preview" className="w-full rounded-lg object-cover max-h-40 mt-2" onError={e => (e.currentTarget.style.display = 'none')} />
            )}
          </div>

          {/* Tags */}
          <div className={sectionCls}>
            <h2 className="text-sm font-semibold text-slate-300 border-b border-slate-800 pb-2">Tags</h2>
            <div>
              <label className={labelCls}>Tags (JSON array or comma-separated)</label>
              <input type="text" className={fieldCls} placeholder='["iPhone repair", "Mumbai"]' value={form.tags || ''} onChange={e => set('tags', e.target.value)} />
            </div>
          </div>

          {/* Targeting */}
          <div className={sectionCls}>
            <h2 className="text-sm font-semibold text-slate-300 border-b border-slate-800 pb-2">SEO Targeting</h2>
            <div>
              <label className={labelCls}>City</label>
              <input type="text" className={fieldCls} placeholder="Mumbai" value={form.city || ''} onChange={e => set('city', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Area / Locality</label>
              <input type="text" className={fieldCls} placeholder="Andheri" value={form.area || ''} onChange={e => set('area', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Device</label>
              <input type="text" className={fieldCls} placeholder="iPhone 15 Pro" value={form.device || ''} onChange={e => set('device', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Service</label>
              <input type="text" className={fieldCls} placeholder="Battery Replacement" value={form.service || ''} onChange={e => set('service', e.target.value)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogFormPage;
